// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * "Importér BIM-model" as data (frontend-ayw.4 Speckle, frontend-ayw.9 IFC;
 * ruxd PR #12): a pasted Speckle link read into ids, the checks before
 * `POST /imports/speckle` and before an IFC upload starts, the chunks the
 * upload goes up in, the Danish line for each refusal, and what a finished
 * import job wrote — said as one line and as the resources to re-read.
 * Pure: no React, no DOM.
 */

import { ApiRequestError } from '../api/client';
import type { IfcImportOptions, Job, JobResult, SpeckleImportRequest, SpeckleTokenStatus } from '../api/types';
import { formatBytes, uploadChunks, type UploadChunk } from '../app/cases';
import type { Check } from '../konto/model';
import { speckleHost } from '../konto/speckle';

// ------------------------------------------------------------- stages --

/** ReUseX's import stages, as `Job.stage` names them. */
export const IMPORT_STAGES = ['import_speckle', 'import_ifc'] as const;
export type ImportStage = (typeof IMPORT_STAGES)[number];

export function isImportStage(stage: string): stage is ImportStage {
  return (IMPORT_STAGES as readonly string[]).includes(stage);
}

/** The job's Danish name, where the jobs UI shows a stage. */
export const IMPORT_STAGE_LABEL: Record<ImportStage, string> = {
  import_speckle: 'Speckle-import',
  import_ifc: 'IFC-import',
};

/** A job's stage for display: the import stages in Danish, every other stage as it is. */
export function jobStageLabel(stage: string): string {
  return isImportStage(stage) ? IMPORT_STAGE_LABEL[stage] : stage;
}

// ------------------------------------------------------- speckle link --

/** A Speckle model, as a link or the form names it. */
export interface SpeckleRef {
  /** The link's `scheme://host[:port]`; null when the ids were typed. */
  server: string | null;
  project: string;
  model: string;
  /** null = the latest version. */
  version: string | null;
}

/** ReUseX's Speckle id: 1–128 letters and digits. */
export function isSpeckleId(text: string): boolean {
  return /^[A-Za-z0-9]{1,128}$/.test(text);
}

/** Whether the text is a link (the form then reads it) rather than an id. */
export function looksLikeSpeckleLink(text: string): boolean {
  return /^\s*https?:\/\//i.test(text);
}

const BAD_LINK = 'Linket kan ikke læses. Kopiér det fra modellens side i Speckle: …/projects/<projekt>/models/<model>.';

/**
 * A Speckle web link → project, model and (after `@`) version:
 * `https://app.speckle.systems/projects/<pid>/models/<mid>[@<vid>]`. A link
 * to several models (`<mid>,<mid2>`), to a whole project or to Speckle's
 * old `streams/` pages is refused with the reason.
 */
export function parseSpeckleLink(raw: string): Check<SpeckleRef> {
  const text = raw.trim();
  let url: URL;
  try {
    url = new URL(text);
  } catch {
    return { ok: false, reason: BAD_LINK };
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return { ok: false, reason: BAD_LINK };
  let segments: string[];
  try {
    segments = url.pathname
      .split('/')
      .filter(Boolean)
      .map((s) => decodeURIComponent(s));
  } catch {
    return { ok: false, reason: BAD_LINK };
  }
  if (segments[0] === 'streams')
    return {
      ok: false,
      reason: 'Det er et link fra Speckles gamle visning (streams/…). Åbn modellen i Speckle, og kopiér linket derfra (projects/…/models/…).',
    };
  if (segments[0] !== 'projects' || segments.length < 2) return { ok: false, reason: BAD_LINK };
  const project = segments[1];
  if (!isSpeckleId(project)) return { ok: false, reason: 'Projekt-id’et i linket er ugyldigt.' };
  if (segments.length < 4 || segments[2] !== 'models')
    return { ok: false, reason: 'Linket peger på et helt projekt. Åbn den model, der skal importeres, og kopiér linket til den.' };
  if (segments.length > 4) return { ok: false, reason: BAD_LINK };
  const spec = segments[3];
  if (spec.includes(','))
    return { ok: false, reason: 'Linket viser flere modeller på én gang. Importér én model ad gangen: åbn den alene, og kopiér linket.' };
  if (spec.toLowerCase() === 'all')
    return { ok: false, reason: 'Linket viser alle projektets modeller. Åbn den model, der skal importeres, og kopiér linket til den.' };
  const at = spec.indexOf('@');
  const model = at < 0 ? spec : spec.slice(0, at);
  const version = at < 0 ? null : spec.slice(at + 1);
  if (!isSpeckleId(model)) return { ok: false, reason: 'Model-id’et i linket er ugyldigt.' };
  if (version !== null && !isSpeckleId(version)) return { ok: false, reason: 'Versions-id’et i linket er ugyldigt.' };
  return { ok: true, value: { server: url.origin, project, model, version } };
}

// ------------------------------------------------------------ options --

/** ReUseX's defaults (`SpeckleRevitImportOptions`, `IfcImportOptions`). */
export const IMPORT_DEFAULTS = { meshes: true, storeyHeight: 3.0, minWallThickness: 0.05 } as const;

/** The form's advanced options, as typed (Danish decimal comma allowed). */
export interface ImportOptionsDraft {
  meshes: boolean;
  storeyHeight: string;
  minWallThickness: string;
}

/** `3` → `3,0`, `0.05` → `0,05`: a default as the field shows it. */
export function danishDecimal(n: number, minDecimals = 1): string {
  const text = n.toFixed(Math.max(minDecimals, (String(n).split('.')[1] ?? '').length));
  return text.replace('.', ',');
}

export const DEFAULT_OPTIONS_DRAFT: ImportOptionsDraft = {
  meshes: IMPORT_DEFAULTS.meshes,
  storeyHeight: danishDecimal(IMPORT_DEFAULTS.storeyHeight),
  minWallThickness: danishDecimal(IMPORT_DEFAULTS.minWallThickness),
};

/** A typed number (`3,5`, `3.5`, ` 3 `) or NaN. */
export function parseDecimal(text: string): number {
  const t = text.trim().replace(',', '.');
  return /^-?\d+(\.\d+)?$|^-?\.\d+$/.test(t) ? Number(t) : Number.NaN;
}

/**
 * The options body: only what differs from ReUseX's default (an omitted key
 * is the library's own default), checked against the contract's bounds.
 */
export function importOptionsCheck(draft: ImportOptionsDraft): Check<IfcImportOptions> {
  const out: IfcImportOptions = {};
  const height = parseDecimal(draft.storeyHeight);
  if (!(height >= 0.5 && height <= 100)) return { ok: false, reason: 'Etagehøjden skal være et tal mellem 0,5 og 100 m.' };
  const wall = parseDecimal(draft.minWallThickness);
  if (!(wall >= 0 && wall <= 2)) return { ok: false, reason: 'Mindste vægtykkelse skal være et tal mellem 0 og 2 m.' };
  if (!draft.meshes) out.meshes = false;
  if (height !== IMPORT_DEFAULTS.storeyHeight) out.storey_height = height;
  if (wall !== IMPORT_DEFAULTS.minWallThickness) out.min_wall_thickness = wall;
  return { ok: true, value: out };
}

// ------------------------------------------------------ speckle check --

/** The Speckle tab as typed. */
export interface SpeckleDraft {
  /** The server of a pasted link (null when the ids were typed). */
  linkServer: string | null;
  project: string;
  model: string;
  /** Empty = the latest version. */
  version: string;
  options: ImportOptionsDraft;
}

/** Whether the saved token can import: set, and (server mode) its server still allowed. */
export function speckleTokenUsable(status: SpeckleTokenStatus | null): boolean {
  return status !== null && status.configured && status.server_allowed;
}

/** The checks before `POST /imports/speckle`; the value is its body (server = the token's). */
export function speckleImportCheck(draft: SpeckleDraft, status: SpeckleTokenStatus | null): Check<SpeckleImportRequest> {
  if (!status || !status.configured)
    return { ok: false, reason: 'Gem først et Speckle-token under Konto — importen henter modellen med det.' };
  if (!status.server_allowed)
    return { ok: false, reason: `Dit Speckle-token er til ${speckleHost(status.server)}, som ikke længere er tilladt her. Gem et token til en tilladt server under Konto.` };
  const project = draft.project.trim();
  const model = draft.model.trim();
  const version = draft.version.trim();
  if (project === '' || model === '') return { ok: false, reason: 'Indsæt et link til modellen, eller skriv projekt- og model-id.' };
  if (!isSpeckleId(project)) return { ok: false, reason: 'Projekt-id’et er ugyldigt (1–128 bogstaver og cifre).' };
  if (!isSpeckleId(model)) return { ok: false, reason: 'Model-id’et er ugyldigt (1–128 bogstaver og cifre).' };
  if (version !== '' && !isSpeckleId(version)) return { ok: false, reason: 'Versions-id’et er ugyldigt (1–128 bogstaver og cifre).' };
  if (draft.linkServer !== null && draft.linkServer !== status.server)
    return {
      ok: false,
      reason: `Linket er til ${speckleHost(draft.linkServer)}, men dit Speckle-token er til ${speckleHost(status.server)}. Gem et token til ${speckleHost(draft.linkServer)} under Konto.`,
    };
  const options = importOptionsCheck(draft.options);
  if (!options.ok) return options;
  return {
    ok: true,
    value: { project, model, ...(version !== '' ? { version } : {}), server: status.server, ...options.value },
  };
}

// ---------------------------------------------------------------- ifc --

/** ruxd's default `max_bytes` (1 GiB), before an upload has told us its own. */
export const IFC_MAX_BYTES = 1024 ** 3;
/** The client's own chunk ceiling: progress and Annullér move at least this often. */
export const IFC_CLIENT_CHUNK_BYTES = 8 * 1024 ** 2;
/** How many leading bytes the header check reads (ruxd looks for `HEADER;` within the first KiB). */
export const IFC_HEAD_BYTES = 1024;

const ISO_HEADER = 'ISO-10303-21;';

/**
 * Whether a file's first bytes are an ISO 10303-21 (STEP) header, as ruxd
 * checks it at complete: an optional UTF-8 BOM, whitespace, `ISO-10303-21;`,
 * and `HEADER;` within the first KiB. (ruxd also wants a `FILE_SCHEMA`,
 * which may lie further in; it answers 422 without one.)
 */
export function hasIfcHeader(head: Uint8Array): boolean {
  if (!startsIso(head)) return false;
  const text = String.fromCharCode(...head.subarray(0, IFC_HEAD_BYTES));
  return text.includes('HEADER;');
}

function startsIso(head: Uint8Array): boolean {
  let i = 0;
  if (head[0] === 0xef && head[1] === 0xbb && head[2] === 0xbf) i = 3;
  while (i < head.length && (head[i] === 0x20 || (head[i] >= 0x09 && head[i] <= 0x0d))) i += 1;
  if (head.length - i < ISO_HEADER.length) return false;
  for (let k = 0; k < ISO_HEADER.length; k += 1) if (head[i + k] !== ISO_HEADER.charCodeAt(k)) return false;
  return true;
}

/** The checks on a picked file before its upload starts. `head` = its first {@link IFC_HEAD_BYTES} bytes. */
export function ifcFileCheck(file: { name: string; size: number }, head: Uint8Array, maxBytes = IFC_MAX_BYTES): Check<null> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.ifczip') || name.endsWith('.ifcxml'))
    return { ok: false, reason: 'Kun IFC i tekstformat (.ifc) kan importeres. Gem eller pak modellen ud som .ifc.' };
  if (!name.endsWith('.ifc')) return { ok: false, reason: 'Vælg en IFC-fil (.ifc).' };
  if (file.size === 0) return { ok: false, reason: 'Filen er tom.' };
  if (file.size > maxBytes)
    return { ok: false, reason: `Filen fylder ${formatBytes(file.size)} — serveren tager højst ${formatBytes(maxBytes)}.` };
  if (!hasIfcHeader(head))
    return { ok: false, reason: 'Filen er ikke en IFC-fil: den begynder ikke med ISO-10303-21-hovedet.' };
  return { ok: true, value: null };
}

/**
 * The chunks an IFC file goes up in: never larger than the server's
 * `chunk_bytes`, nor than {@link IFC_CLIENT_CHUNK_BYTES} (so a 64 MiB server
 * chunk does not leave the bar still for a minute), resuming after `received`.
 */
export function ifcChunks(size: number, serverChunkBytes: number, received = 0): UploadChunk[] {
  return uploadChunks(size, Math.min(serverChunkBytes, IFC_CLIENT_CHUNK_BYTES), received);
}

/**
 * The resume planner: the next chunk to send when the server holds
 * `received` bytes (its own count, from the last `PUT` or a `GET`), or null
 * when the file is complete.
 */
export function nextIfcChunk(size: number, serverChunkBytes: number, received: number): UploadChunk | null {
  if (!(serverChunkBytes > 0)) throw new RangeError('chunkBytes must be positive');
  if (received >= size) return null;
  const start = Math.max(0, received);
  return { start, end: Math.min(size, start + Math.min(serverChunkBytes, IFC_CLIENT_CHUNK_BYTES)) };
}

/** The waits before each retry of a chunk that failed on the network or with a 5xx. */
export const CHUNK_RETRY_DELAYS_MS: readonly number[] = [500, 1500, 4000];
/** How many times in a row the upload may re-sync with the server (`GET`) without the server gaining a byte. */
export const MAX_RESYNCS = 3;

/** Whether a failed chunk is worth sending again: a dropped connection or a server error, never an abort. */
export function isRetryableUploadError(cause: unknown): boolean {
  if (cause instanceof DOMException && cause.name === 'AbortError') return false;
  if (cause instanceof ApiRequestError) return cause.status >= 500;
  return cause instanceof TypeError;
}

/** What {@link sendIfcChunks} needs of the server and the clock (injected, so it is testable). */
export interface ChunkTransport {
  /** `PUT ?offset=start` with bytes [start, end); the answer's `received` is the server's count. */
  put(start: number, end: number): Promise<{ received: number }>;
  /** `GET` the upload: where the server is. */
  status(): Promise<{ received: number }>;
  sleep(ms: number): Promise<void>;
}

/**
 * What {@link sendIfcChunks} is doing between two `onProgress` calls, for the
 * dialog's "prøver igen …" line (frontend-k8n): retrying the same chunk after
 * a dropped connection or a 5xx, or resyncing with the server (a 409, or the
 * retries spent) to find out where it really is.
 */
export type UploadNotice = { kind: 'retrying'; attempt: number; of: number } | { kind: 'resyncing' };

/**
 * Sends the file's bytes from `received` on. Each chunk is retried with
 * backoff ({@link CHUNK_RETRY_DELAYS_MS}) on a network error or a 5xx; on a
 * 409 (the offsets disagree), or once the retries are spent, it asks the
 * server where it is and resumes from there. The next offset is always the
 * server's `received`, never the client's guess. Gives up after
 * {@link MAX_RESYNCS} re-syncs without progress, and at once on any other
 * refusal or an abort. `onNotice`, when given, is told before each retry's
 * wait and before each resync — never on the attempt that succeeds outright.
 */
export async function sendIfcChunks(
  size: number,
  serverChunkBytes: number,
  received: number,
  transport: ChunkTransport,
  onProgress: (received: number) => void,
  signal?: AbortSignal,
  onNotice?: (notice: UploadNotice) => void,
): Promise<void> {
  let at = received;
  let resyncs = 0;
  for (let chunk = nextIfcChunk(size, serverChunkBytes, at); chunk; chunk = nextIfcChunk(size, serverChunkBytes, at)) {
    const before = at;
    for (let attempt = 0; ; attempt += 1) {
      try {
        at = (await transport.put(chunk.start, chunk.end)).received;
        break;
      } catch (cause) {
        if (signal?.aborted) throw cause;
        const conflict = cause instanceof ApiRequestError && cause.status === 409;
        const retryable = isRetryableUploadError(cause);
        if (!conflict && !retryable) throw cause;
        if (retryable && attempt < CHUNK_RETRY_DELAYS_MS.length) {
          onNotice?.({ kind: 'retrying', attempt: attempt + 1, of: CHUNK_RETRY_DELAYS_MS.length });
          await transport.sleep(CHUNK_RETRY_DELAYS_MS[attempt]);
          if (signal?.aborted) throw cause;
          continue;
        }
        if (resyncs >= MAX_RESYNCS) throw cause;
        resyncs += 1;
        onNotice?.({ kind: 'resyncing' });
        at = (await transport.status()).received;
        break;
      }
    }
    if (at > before) resyncs = 0;
    onProgress(at);
  }
}

/** The upload's percent, as {@link uploadProgressText} and {@link uploadNoticeText} round it. */
function uploadPercent(sent: number, total: number): number {
  return total > 0 ? Math.min(100, Math.floor((sent / total) * 100)) : 0;
}

/** `12,4 MB af 210 MB · 5 %`. */
export function uploadProgressText(sent: number, total: number): string {
  return `${formatBytes(sent)} af ${formatBytes(total)} · ${uploadPercent(sent, total)} %`;
}

/**
 * The line under the progress bar while a chunk is being retried or the
 * upload is resyncing with the server (frontend-k8n); null the rest of the
 * time, so the dialog shows nothing extra. `sent`/`total` are the last known
 * progress — a resync's "Genoptager fra N %" is that, not yet the server's
 * answer, which `onProgress` reports once it comes back.
 */
export function uploadNoticeText(notice: UploadNotice | null, sent: number, total: number): string | null {
  if (!notice) return null;
  if (notice.kind === 'retrying') return `Forbindelsen svigtede — prøver igen (${notice.attempt}/${notice.of})…`;
  return `Genoptager fra ${uploadPercent(sent, total)} %…`;
}

// ------------------------------------------------------------- errors --

/** Which request failed: the Speckle import, or a step of the IFC upload. */
export type ImportStep = 'speckle' | 'ifc-begin' | 'ifc-chunk' | 'ifc-complete';

/**
 * The Danish line under a refused import request. Code-first (`cause.code`,
 * ruxd-202) with the status/message checks kept as the fallback for a server
 * that predates it (frontend-78b) — each branch below tries the code before
 * falling back to the message regex it replaces.
 */
export function importErrorText(cause: unknown, step: ImportStep): string {
  if (!(cause instanceof ApiRequestError)) return 'Kunne ikke nå serveren. Tjek forbindelsen, og prøv igen.';
  const { message, code } = cause;
  switch (cause.status) {
    case 400:
      if (step === 'speckle') {
        if (code === 'speckle_no_token' || /no Speckle token is saved/i.test(message))
          return 'Du har ikke et brugbart Speckle-token (intet gemt, eller dets server er ikke længere tilladt). Gem et under Konto.';
        if (code === 'speckle_server_mismatch' || /your Speckle token is for/i.test(message))
          return 'Dit Speckle-token er til en anden Speckle-server end modellens. Gem et token til den server under Konto.';
        if (code === 'speckle_server_not_allowed' || /does not allow Speckle server|not allowed/i.test(message))
          return 'Den Speckle-server er ikke tilladt på denne server. En administrator tillader servere med --speckle-allowed-server.';
        // `invalid_parameters` also covers a bad option (storey height, wall
        // thickness), so it stays a message check — unlike the three codes
        // above, it is not specific enough on its own to pick this line.
        if (/not a Speckle id/i.test(message)) return 'Projekt-, model- eller versions-id’et er ugyldigt (1–128 bogstaver og cifre).';
        return 'Serveren afviste importen. Tjek id’erne og de avancerede indstillinger.';
      }
      if (step === 'ifc-complete') return 'Serveren afviste importens indstillinger. Tjek de avancerede indstillinger.';
      return 'Serveren afviste uploaden.';
    case 401:
      return 'Du er ikke logget ind længere. Log ind igen, og prøv så igen.';
    case 403:
      if (step === 'speckle' && (code === 'speckle_account_required' || /signed-in user/i.test(message)))
        return 'Speckle-import kræver, at du er logget ind med din egen konto og dit eget Speckle-token — ikke med et token til én sag eller et delingslink.';
      return 'Du har kun læseadgang til sagen. Kun redaktører og ejere kan importere.';
    case 404:
      return step === 'ifc-chunk' || step === 'ifc-complete'
        ? 'Uploaden findes ikke længere (den er afbrudt eller udløbet). Vælg filen igen.'
        : 'Sagen findes ikke længere.';
    case 409:
      if (code === 'upload_incomplete') return 'Ikke hele filen nåede frem. Vælg filen igen.';
      if (code === 'upload_offset_mismatch') return 'Uploaden kom ud af trit med serveren. Vælg filen igen.';
      return step === 'ifc-complete'
        ? 'Ikke hele filen nåede frem. Vælg filen igen.'
        : 'Uploaden kom ud af trit med serveren. Vælg filen igen.';
    case 413:
      return 'Filen er for stor, eller sagens plads til importerede IFC-filer er brugt op (højst 1 GB pr. fil).';
    case 422:
      return 'Filen er ikke en IFC-fil (den mangler ISO-10303-21-hovedet), så serveren har slettet den.';
    case 429:
      return 'Der er for mange IFC-uploads i gang lige nu, på sagen eller på serveren. Vent, til en af dem er færdig.';
    case 507:
      return 'Serveren har ikke plads til filen lige nu. Prøv igen senere, eller kontakt administratoren.';
  }
  return step === 'speckle' ? 'Importen kunne ikke startes.' : step === 'ifc-complete' ? 'Importen kunne ikke startes.' : 'Uploaden fejlede.';
}

/** The Danish line for a failed or cancelled import job (ruxd's own reason after it). */
export function importJobErrorText(job: Job): string {
  const label = isImportStage(job.stage) ? IMPORT_STAGE_LABEL[job.stage] : 'Importen';
  if (job.status === 'cancelled') return `${label} blev annulleret.`;
  const reason = job.error?.trim();
  return reason ? `${label} fejlede: ${reason}` : `${label} fejlede.`;
}

// ------------------------------------------------------------ outputs --

const TABLE_WORDS: readonly { table: string; one: string; many: string }[] = [
  { table: 'storeys', one: 'etage', many: 'etager' },
  { table: 'walls', one: 'væg', many: 'vægge' },
  { table: 'openings', one: 'åbning', many: 'åbninger' },
  { table: 'spaces', one: 'rum', many: 'rum' },
];

/**
 * A finished import's one line: `Importeret: 5 etager, 579 vægge, 186
 * åbninger, 38 rum` from `result.outputs`. A table without a count (not
 * measured) is left out rather than shown as 0; with none at all the
 * model layers are counted instead.
 */
export function importSummary(result: JobResult | undefined): string {
  const outputs = result?.outputs ?? [];
  const parts: string[] = [];
  for (const w of TABLE_WORDS) {
    const row = outputs.find((o) => o.kind === 'table' && o.name === w.table);
    if (row?.count === undefined) continue;
    parts.push(`${row.count.toLocaleString('da-DK')} ${row.count === 1 ? w.one : w.many}`);
  }
  if (parts.length === 0) {
    const meshes = outputs.filter((o) => o.kind === 'mesh').length;
    if (meshes > 0) parts.push(`${meshes} modellag`);
  }
  return parts.length > 0 ? `Importeret: ${parts.join(', ')}` : 'Importeret.';
}

/** What a finished job wrote that the case screens show, so they re-read just that. */
export interface ImportRefresh {
  storeys: boolean;
  spaces: boolean;
  walls: boolean;
  openings: boolean;
  /** Mesh names written (`imported/revit/*`, `imported/ifc/*`). */
  meshes: string[];
}

export function importRefresh(result: JobResult | undefined): ImportRefresh {
  const outputs = result?.outputs ?? [];
  const table = (name: string) => outputs.some((o) => o.kind === 'table' && o.name === name);
  return {
    storeys: table('storeys'),
    spaces: table('spaces'),
    walls: table('walls'),
    openings: table('openings'),
    meshes: outputs.filter((o) => o.kind === 'mesh').map((o) => o.name),
  };
}

/**
 * The import jobs that succeeded since `seen` was taken (ids already
 * finished then), so a screen reacts to an import finishing now — never to
 * one from before it opened, which the `hello` snapshot also carries.
 */
export function newlySucceededImports(seen: ReadonlySet<string>, jobs: readonly Job[]): Job[] {
  return jobs.filter((j) => isImportStage(j.stage) && j.status === 'succeeded' && !seen.has(j.id));
}
